import { createClient } from '@supabase/supabase-js';
import { AppError } from '../errors/AppError.js';
import { logAuthEvent } from '../debug/authDebugLog.js';
import { createUuid } from '../utils/uuid.js';

if (typeof window !== 'undefined' && !(window as unknown as Record<string, unknown>).__authWarnPatched) {
  (window as unknown as Record<string, unknown>).__authWarnPatched = true;
  const origWarn = console.warn.bind(console);
  console.warn = (...args: unknown[]) => {
    origWarn(...args);
    const msg = String(args[0] ?? '');
    if (msg.includes('GoTrueClient') || msg.includes('Multiple')) {
      logAuthEvent('console.warn', args.map(String));
    }
  };
}

const env = (import.meta as ImportMeta & { env: Record<string, string | undefined> }).env;

const supabaseUrl = env.VITE_SUPABASE_URL ?? env.SUPABASE_URL;
const supabasePublishableKey =
  env.VITE_SUPABASE_PUBLISHABLE_KEY ??
  env.SUPABASE_PUBLISHABLE_KEY ??
  env.VITE_SUPABASE_ANON_KEY ??
  env.SUPABASE_ANON_KEY;

type SupabaseClient = ReturnType<typeof createClient>;

type SupabaseClientGlobal = typeof globalThis & {
  __aimaxSupabaseClient__?: SupabaseClient;
  __aimaxSupabaseClientId__?: string;
};

const globalScope = globalThis as SupabaseClientGlobal;
const hmr = (import.meta as ImportMeta & {
  hot?: {
    data?: { supabaseClient?: SupabaseClient };
    dispose(callback: (data: { supabaseClient?: SupabaseClient }) => void): void;
  };
}).hot;

// A module-level/HMR cache is insufficient when the production bundle contains
// more than one copy of this module. Keep exactly one GoTrueClient per page on
// globalThis so all chunks share the same refresh timer and auth storage owner.
let cachedClient: SupabaseClient | null =
  globalScope.__aimaxSupabaseClient__ ??
  hmr?.data?.supabaseClient ??
  null;

function getClientId(): string {
  if (!globalScope.__aimaxSupabaseClientId__) {
    globalScope.__aimaxSupabaseClientId__ = createUuid();
  }
  return globalScope.__aimaxSupabaseClientId__;
}

export function getSupabaseClient() {
  if (!supabaseUrl || !supabasePublishableKey) {
    throw new AppError(
      'Missing Supabase env vars. Please set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY.',
      'SUPABASE_CONFIG_MISSING',
    );
  }

  if (!cachedClient) {
    cachedClient = createClient(supabaseUrl, supabasePublishableKey);
    globalScope.__aimaxSupabaseClient__ = cachedClient;
    logAuthEvent('client:create', { url: supabaseUrl, clientId: getClientId() });
  } else if (!globalScope.__aimaxSupabaseClient__) {
    // Adopt the HMR client into the per-page singleton store.
    globalScope.__aimaxSupabaseClient__ = cachedClient;
  }

  return cachedClient;
}

if (hmr) {
  hmr.dispose((data: { supabaseClient?: SupabaseClient }) => {
    data.supabaseClient = cachedClient;
  });
}
